import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Table, Badge, Pagination } from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL } from '../App';
import '../styles/transactions.css';

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gradeLevelStats, setGradeLevelStats] = useState([]);
  const [walletStats, setWalletStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('customer_name');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-01-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTransactions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTransactions = async (page = 1) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // بناء URL مع معاملات البحث
      let url = `${BASE_URL}/api/transactions?page=${page}`;
      
      // إضافة معاملات التاريخ
      url += `&start_date=${startDate}&end_date=${endDate}`;
      
      // إضافة معاملات البحث
      if (searchTerm.trim()) {
        const searchValue = encodeURIComponent(searchTerm.trim());
        // البحث في الحقل المحدد
        url += `&${searchField}=${searchValue}`;
      }
      
      // إضافة فلتر الحالة
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      // إضافة معاملات الترتيب
      url += `&sort_by=created_at&sort_order=desc`;
      
      console.log('Fetching URL:', url); // للتشخيص
      
      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          "ngrok-skip-browser-warning": "any",
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('API Response:', response.data); // للتشخيص
      
      setTransactions(response.data.data.transactions || []);
      setPagination(response.data.data.pagination || {});
      setGradeLevelStats(response.data.data.grade_level_statistics?.grade_levels || []);
      setWalletStats(response.data.data.grade_level_statistics?.wallet_statistics || null);
      setCurrentPage(page);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('حدث خطأ في تحميل البيانات');
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      fetchTransactions(page);
    }
  };

  const handleDateChange = () => {
    fetchTransactions(1);
  };

  const handleSearch = () => {
    fetchTransactions(1);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setSearchField('customer_name');
    setStatusFilter('');
    fetchTransactions(1);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: 'UTC',
        numberingSystem: 'arab'
      };
      const formatter = new Intl.DateTimeFormat('ar-EG', options);
      return formatter.format(date);
    } catch (e) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PAID':
        return <Badge bg="success">مدفوع</Badge>;
      case 'PENDING':
        return <Badge bg="info">بانتظار الدفع</Badge>;
      case 'failed':
        return <Badge bg="danger">فشل</Badge>;
      case 'cancelled':
        return <Badge bg="secondary">ملغي</Badge>;
      default:
        return <Badge bg="info">{status}</Badge>;
    }
  };


  const getTransactionTypeText = (type) => {
    switch (type) {
      case 'wallet_charge':
        return 'شحن محفظة';
      case 'course_purchase':
        return 'شراء كورس';
      case 'subscription':
        return 'اشتراك';
      default:
        return type || 'غير محدد';
    }
  };

  if (isLoading) {
    return (
      <div className="transactions-loading">
        <div className="loading-content">
          <h2>جاري تحميل بيانات المدفوعات...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transactions-error">
        <div className="error-content">
          <h2>خطأ في تحميل البيانات</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transactions-page">
      <Container fluid className="px-4">
        <Row>
          <Col lg={12}>
            <div className="transactions-header">
              <h1>المدفوعات الإلكترونية</h1>
              <p>عرض جميع المعاملات المالية في المنصة</p>
            </div>

            {/* Date Filters */}
            <Card className="date-filters-card mb-3">
              <Card.Body className="py-3">
                <div className="filters-header">
                  <h4 className="filters-title">
                    <i className="fas fa-calendar-alt me-2"></i>
                    فلترة المعاملات حسب التاريخ
                  </h4>
                  <p className="filters-subtitle">اختر النطاق الزمني لعرض البيانات</p>
                </div>
                
                <Row className="filters-content">
                  {/* Date Inputs Section */}
                  <Col lg={6}>
                    <Row>
                      <Col md={6}>
                        <div className="date-input-group">
                          <label className="form-label">
                            <i className="fas fa-play me-1"></i>
                            من تاريخ:
                          </label>
                          <input
                            type="date"
                            className="form-control date-input"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="date-input-group">
                          <label className="form-label">
                            <i className="fas fa-stop me-1"></i>
                            إلى تاريخ:
                          </label>
                          <input
                            type="date"
                            className="form-control date-input"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Col>

                  {/* Quick Dates Section */}
                  <Col lg={3}>
                    <div className="quick-dates">
                      <label className="form-label">تواريخ سريعة:</label>
                      <div className="quick-date-buttons">
                        <button
                          className="btn btn-sm btn-outline-light mb-2"
                          onClick={() => {
                            const today = new Date();
                            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                            setStartDate(weekAgo.toISOString().split('T')[0]);
                            setEndDate(today.toISOString().split('T')[0]);
                          }}
                        >
                          آخر أسبوع
                        </button>
                        <button
                          className="btn btn-sm btn-outline-light"
                          onClick={() => {
                            const today = new Date();
                            const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
                            setStartDate(monthAgo.toISOString().split('T')[0]);
                            setEndDate(today.toISOString().split('T')[0]);
                          }}
                        >
                          آخر شهر
                        </button>
                      </div>
                    </div>
                  </Col>

                  {/* Update Button Section */}
                  <Col lg={3}>
                    <div className="update-section">
                      <button
                        className="btn btn-primary update-btn w-100"
                        onClick={handleDateChange}
                      >
                        <i className="fas fa-sync-alt me-2"></i>
                        تحديث البيانات
                      </button>
                    </div>
                  </Col>
                </Row>
                
                <div className="date-range-info">
                  <span className="range-text">
                    <i className="fas fa-info-circle me-1"></i>
                    النطاق المحدد: من {startDate} إلى {endDate}
                  </span>
                </div>
              </Card.Body>
            </Card>

            {/* Search Section */}
            <Card className="search-card mb-3">
              <Card.Body className="py-3">
                <div className="search-header">
                  <h4 className="search-title">
                    <i className="fas fa-search me-2"></i>
                    البحث في المعاملات
                  </h4>
                  <p className="search-subtitle">ابحث في المعاملات باستخدام معايير مختلفة</p>
                </div>
                
                <Row className="search-content">
                  <Col lg={3}>
                    <div className="search-input-group">
                      <label className="form-label">
                        <i className="fas fa-keyboard me-1"></i>
                        كلمة البحث:
                      </label>
                      <input
                        type="text"
                        className="form-control search-input"
                        placeholder="أدخل كلمة البحث..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      />
                    </div>
                  </Col>
                  
                  <Col lg={2}>
                    <div className="search-field-group">
                      <label className="form-label">
                        <i className="fas fa-search me-1"></i>
                        نوع البحث:
                      </label>
                      <select
                        className="form-select search-input"
                        value={searchField}
                        onChange={(e) => setSearchField(e.target.value)}
                      >
                        <option value="customer_name">اسم العميل</option>
                        <option value="customer_email">البريد الإلكتروني</option>
                        <option value="customer_phone">رقم الهاتف</option>
                        <option value="transaction_id">رقم المعاملة</option>
                        <option value="order_id">رقم الطلب</option>
                      </select>
                    </div>
                  </Col>
                  
                  <Col lg={2}>
                    <div className="status-filter-group">
                      <label className="form-label">
                        <i className="fas fa-filter me-1"></i>
                        حالة المعاملة:
                      </label>
                      <select
                        className="form-select search-input"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="">جميع الحالات</option>
                        <option value="PAID">مدفوع</option>
                        <option value="PENDING">بانتظار الدفع</option>
                        <option value="failed">فشل</option>
                        <option value="cancelled">ملغي</option>
                      </select>
                    </div>
                  </Col>
                  
                  <Col lg={3}>
                    <div className="search-buttons">
                      <button
                        className="btn btn-primary search-btn me-2"
                        onClick={handleSearch}
                      >
                        <i className="fas fa-search me-1"></i>
                        بحث
                      </button>
                      <button
                        className="btn btn-outline-secondary clear-btn"
                        onClick={handleSearchClear}
                      >
                        <i className="fas fa-times me-1"></i>
                        مسح
                      </button>
                    </div>
                  </Col>
                  
                  <Col lg={3}>
                    <div className="search-info">
                      {(searchTerm || statusFilter) && (
                        <div className="search-status">
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            {searchTerm && `البحث: "${searchTerm}"`}
                            {statusFilter && ` | الحالة: ${statusFilter}`}
                          </small>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Statistics Section */}
            {(gradeLevelStats.length > 0 || walletStats) && (
              <div className="statistics-section mb-3">
                <div className="statistics-header">
                  <h3 className="statistics-title">
                    <i className="fas fa-chart-bar me-3"></i>
                    إحصائيات المعاملات
                  </h3>
                  <div className="statistics-decoration">
                    <div className="decoration-line"></div>
                    <div className="decoration-circle"></div>
                    <div className="decoration-line"></div>
                  </div>
                </div>
                
                {/* Grade Level Statistics */}
                {gradeLevelStats.length > 0 && (
                  <Row className="mb-3">
                    <Col lg={12}>
                      <h5 className="statistics-subtitle">
                        <i className="fas fa-graduation-cap me-2"></i>
                        إحصائيات المستويات الدراسية
                      </h5>
                      <div className="grade-stats-container">
                        {gradeLevelStats.map((grade, index) => (
                          <div key={grade.grade_level} className="grade-stat-item" data-aos="fade-up" data-aos-delay={index * 100}>
                            <Card className="stat-card h-100">
                              <Card.Body className="text-center py-3">
                                <div className="grade-icon">
                                  <i className="fas fa-book-open"></i>
                                </div>
                                <h6 className="grade-title">{grade.grade_level_display}</h6>
                                <div className="stat-numbers">
                                  <div className="stat-row">
                                    <span className="stat-label">
                                      <i className="fas fa-list me-1"></i>
                                      إجمالي المعاملات:
                                    </span>
                                    <span className="stat-value">{grade.total_transactions}</span>
                                  </div>
                                  <div className="stat-row">
                                    <span className="stat-label">
                                      <i className="fas fa-check-circle me-1"></i>
                                      المدفوعات:
                                    </span>
                                    <span className="stat-value text-success">{grade.paid_transactions}</span>
                                  </div>
                                  <div className="stat-row">
                                    <span className="stat-label">
                                      <i className="fas fa-clock me-1"></i>
                                      بانتظار الدفع:
                                    </span>
                                    <span className="stat-value text-info">{grade.pending_transactions}</span>
                                  </div>
                                  <div className="stat-row">
                                    <span className="stat-label">
                                      <i className="fas fa-times-circle me-1"></i>
                                      الفاشلة:
                                    </span>
                                    <span className="stat-value text-danger">{grade.failed_transactions}</span>
                                  </div>
                                  <div className="stat-row">
                                    <span className="stat-label">
                                      <i className="fas fa-money-bill-wave me-1"></i>
                                      إجمالي المبلغ:
                                    </span>
                                    <span className="stat-value text-primary">{grade.total_amount} جنيه</span>
                                  </div>
                                  <div className="stat-row">
                                    <span className="stat-label">
                                      <i className="fas fa-chart-line me-1"></i>
                                      معدل المبيعات:
                                    </span>
                                    <span className="stat-value text-success">{grade.success_rate}%</span>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Wallet Statistics */}
                {walletStats && (
                  <Row>
                    <Col lg={12}>
                      <h5 className="statistics-subtitle">
                        <i className="fas fa-wallet me-2"></i>
                        إحصائيات شحن المحفظة
                      </h5>
                      <Card className="wallet-stat-card" data-aos="zoom-in">
                        <Card.Body className="py-3">
                          <Row>
                            <Col md={3} className="text-center">
                              <div className="wallet-stat-item">
                                <div className="stat-icon">
                                  <i className="fas fa-list-alt"></i>
                                </div>
                                <h4 className="stat-number">{walletStats.total_transactions}</h4>
                                <p className="stat-label">إجمالي المعاملات</p>
                              </div>
                            </Col>
                            <Col md={3} className="text-center">
                              <div className="wallet-stat-item">
                                <div className="stat-icon success">
                                  <i className="fas fa-check-circle"></i>
                                </div>
                                <h4 className="stat-number text-success">{walletStats.paid_transactions}</h4>
                                <p className="stat-label">المدفوعات</p>
                              </div>
                            </Col>
                            <Col md={3} className="text-center">
                              <div className="wallet-stat-item">
                                <div className="stat-icon info">
                                  <i className="fas fa-clock"></i>
                                </div>
                                <h4 className="stat-number text-info">{walletStats.pending_transactions}</h4>
                                <p className="stat-label">بانتظار الدفع</p>
                              </div>
                            </Col>
                            <Col md={3} className="text-center">
                              <div className="wallet-stat-item">
                                <div className="stat-icon primary">
                                  <i className="fas fa-money-bill-wave"></i>
                                </div>
                                <h4 className="stat-number text-primary">{walletStats.total_amount} جنيه</h4>
                                <p className="stat-label">إجمالي المبلغ</p>
                              </div>
                            </Col>
                          </Row>
                          <Row className="mt-2">
                            <Col md={6} className="text-center">
                              <div className="wallet-stat-item">
                                <div className="stat-icon danger">
                                  <i className="fas fa-times-circle"></i>
                                </div>
                                <h4 className="stat-number text-danger">{walletStats.failed_transactions}</h4>
                                <p className="stat-label">المعاملات الفاشلة</p>
                              </div>
                            </Col>
                            <Col md={6} className="text-center">
                              <div className="wallet-stat-item">
                                <div className="stat-icon success">
                                  <i className="fas fa-chart-line"></i>
                                </div>
                                <h4 className="stat-number text-success">{walletStats.success_rate}%</h4>
                                <p className="stat-label">معدل المبيعات</p>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            )}

            <Card className="transactions-card">
              <Card.Body className="py-3">
                {transactions.length === 0 ? (
                  <div className="no-transactions">
                    <h3>لا توجد معاملات حالياً</h3>
                    <p>لم يتم تسجيل أي مدفوعات إلكترونية بعد</p>
                  </div>
                ) : (
                  <div className="transactions-table-container">
                    <Table responsive striped hover className="transactions-table">
                     <thead>
                       <tr>
                         <th>رقم المعاملة</th>
                         <th>المستخدم</th>
                         <th>المبلغ</th>
                         <th>طريقة الدفع</th>
                         <th>نوع المعاملة</th>
                         <th>الحالة</th>
                         <th>تاريخ المعاملة</th>
                         <th>تفاصيل إضافية</th>
                       </tr>
                     </thead>
                     <tbody>
                       {transactions.map((transaction) => (
                         <tr key={transaction.id}>
                           <td>
                             <strong>#{transaction.id}</strong>
                             <br />
                             <small className="order-id">طلب: {transaction.order_id}</small>
                           </td>
                           <td>
                             <div className="user-info">
                               <span className="user-name">
                                 {transaction.customer?.name || transaction.user?.name || 'غير محدد'}
                               </span>
                               <small className="user-email">
                                 {transaction.customer?.email || transaction.user?.email || 'غير محدد'}
                               </small>
                               <small className="user-phone">
                                 {transaction.customer?.phone || transaction.user?.phone || 'غير محدد'}
                               </small>
                             </div>
                           </td>
                           <td>
                             <span className="amount">
                               {transaction.total} جنيه
                             </span>
                           </td>
                           <td>
                             <span className="payment-method">
                               {transaction.payment_method || 'غير محدد'}
                             </span>
                           </td>
                           <td>
                             <span className="transaction-type">
                               {getTransactionTypeText(transaction.transaction_type)}
                             </span>
                           </td>
                           <td>
                             {getStatusBadge(transaction.status)}
                           </td>
                           <td>
                             <span className="transaction-date">
                               {formatDate(transaction.created_at)}
                             </span>
                           </td>
                           <td>
                             <div className="transaction-details">
                               {transaction.fawryCode && (
                                 <div className="fawry-code">
                                   <strong>كود فوري:</strong> {transaction.fawryCode}
                                 </div>
                               )}
                               {transaction.merchantRefNum && (
                                 <div className="reference-id">
                                   <strong>رقم المرجع:</strong> {transaction.merchantRefNum}
                                 </div>
                               )}
                               {transaction.fawryRefNumber && (
                                 <div className="fawry-ref">
                                   <strong>رقم فوري:</strong> {transaction.fawryRefNumber}
                                 </div>
                               )}
                               {transaction.course_name && (
                                 <div className="course-name">
                                   <strong>اسم الكورس:</strong> {transaction.course_name}
                                 </div>
                               )}
                               {transaction.paid_at && (
                                 <div className="paid-at">
                                   <strong>تاريخ الدفع:</strong> {formatDate(transaction.paid_at)}
                                 </div>
                               )}
                             </div>
                           </td>
                         </tr>
                       ))}
                     </tbody>
                    </Table>
                    
                    {/* Pagination */}
                    {pagination.last_page > 1 && (
                      <div className="pagination-container">
                        <Pagination className="justify-content-center">
                          {/* First Page */}
                          <Pagination.First 
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          />
                          
                          {/* Previous Page */}
                          <Pagination.Prev 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          
                          {/* Page Numbers */}
                          {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                            .filter(page => {
                              // Show current page, first page, last page, and pages around current
                              return page === 1 || 
                                     page === pagination.last_page || 
                                     Math.abs(page - currentPage) <= 2;
                            })
                            .map((page, index, array) => {
                              // Add ellipsis if there's a gap
                              const prevPage = array[index - 1];
                              const showEllipsis = prevPage && page - prevPage > 1;
                              
                              return (
                                <React.Fragment key={page}>
                                  {showEllipsis && <Pagination.Ellipsis disabled />}
                                  <Pagination.Item
                                    active={page === currentPage}
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </Pagination.Item>
                                </React.Fragment>
                              );
                            })}
                          
                          {/* Next Page */}
                          <Pagination.Next 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.last_page}
                          />
                          
                          {/* Last Page */}
                          <Pagination.Last 
                            onClick={() => handlePageChange(pagination.last_page)}
                            disabled={currentPage === pagination.last_page}
                          />
                        </Pagination>
                        
                        {/* Pagination Info */}
                        <div className="pagination-info">
                          <small>
                            عرض {pagination.from} إلى {pagination.to} من {pagination.total} معاملة
                            (صفحة {pagination.current_page} من {pagination.last_page})
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Transactions;
